import styled from 'styled-components';
import React from 'react';
import type { FactorMetric } from 'venn-api';
import { Tooltip, TooltipPosition } from 'venn-ui-kit';
import PercentageSpan from '../percentage-span/PercentageSpan';

import { find } from 'lodash';

export const TOOLTIP_BACKGROUND = 'rgba(16, 22, 27, 0.9)';
export const WIDTH = 180;
const HEIGHT = 10;

export const getAbsoluteRates = (rates: FactorMetric[]) => rates.map(({ value }) => Math.abs(value * 100));

export const getRatesSum = (absoluteRates: number[]) => absoluteRates.reduce((sum, rate) => sum + rate, 0);

export const getRatesInPixels = (absoluteRates: number[], ratesSum: number): number[] =>
  absoluteRates.map((rate: number) => (rate / ratesSum) * WIDTH);

export const getPositions = (ratesInPixels: number[]) => {
  const positions = [0];

  for (let i = 0, len = ratesInPixels.length - 1; i < len; i++) {
    positions[i + 1] = positions[i] + ratesInPixels[i];
  }

  return positions;
};

export interface RiskBarChartProps {
  data: FactorMetric[];
  comparisonPalette: Record<string, string>;
  extraTooltipContent?: React.ReactNode;
}

const RiskBarChart = ({ comparisonPalette, data, extraTooltipContent }: RiskBarChartProps) => {
  if (data.length < 1 || !comparisonPalette) {
    return null;
  }

  const absoluteRates = getAbsoluteRates(data);
  const ratesSum = getRatesSum(absoluteRates);

  if (ratesSum === 0) {
    return null;
  }

  const ratesInPixels = getRatesInPixels(absoluteRates, ratesSum);
  const positions = getPositions(ratesInPixels);

  return (
    <StyledTooltip
      position={TooltipPosition.Top}
      background={TOOLTIP_BACKGROUND}
      maxWidth={WIDTH + 20}
      content={
        <div>
          <h1>Two Sigma Factor Lens</h1>
          {Object.keys(comparisonPalette).map((factor) => {
            const factorData = find(data, { name: factor });

            if (!factorData) {
              return null;
            }

            return (
              <div className="factor-row" key={factor}>
                <TooltipCircleLabel color={comparisonPalette[factor]} />
                {factor}:<PercentageSpan className="tooltip-value" value={factorData.value} />
              </div>
            );
          })}
          {extraTooltipContent}
        </div>
      }
    >
      <svg className="svg-bar-chart" width={WIDTH} height={HEIGHT} viewBox={`0 0 ${WIDTH} ${HEIGHT}`}>
        {data.map((value, index) => (
          <rect
            className="bar"
            transform={`translate(${positions[index]},0)`}
            key={value.name}
            width={WIDTH}
            height={HEIGHT}
            fill={comparisonPalette[value.name]}
          />
        ))}
      </svg>
    </StyledTooltip>
  );
};
export default RiskBarChart;

const TooltipCircleLabel = styled.div<{ color: string }>`
  display: inline-block;
  height: 8px;
  width: 8px;
  border-radius: 50%;
  border: 1px solid white;
  margin-right: 7px;
  background-color: ${(props) => props.color};
`;

const StyledTooltip = styled(Tooltip)`
  > div {
    left: -10px;

    > div {
      width: 100%;
      position: relative;
    }

    h1 {
      font-weight: bold;
      margin-bottom: 20px;
      font-size: 1.5rem;
    }

    .factor-row {
      margin: 0;
      font-size: 1.2rem;
      line-height: 1.5;
      width: 180px;
    }

    .tooltip-value {
      float: right;
      font-weight: bold;
      padding-left: 13px;
    }
  }
  svg.svg-bar-chart {
    overflow: hidden;
  }
`;
